import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import CreditCostEstimate from '../components/CreditCostEstimate';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrgSwitchRedirect } from '../hooks/useOrgSwitchRedirect';
import { usePlan } from '../context/PlanContext';

import {
  ArrowLeft,
  CircleNotch,
  FileText,
  FloppyDisk,
  Image as ImageIcon,
  Lock,
  Plus,
  Sparkle,
  UploadSimple,
  X
} from '@phosphor-icons/react';
const CreateProcess = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { isReady, activeOrganization, orgLoading } = useOrgSwitchRedirect();
  const { isFreePlan } = usePlan();
  const isBasicUser = isFreePlan();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [showAIExtraction, setShowAIExtraction] = useState(true);
  const [extractionMethod, setExtractionMethod] = useState('text'); // 'text' or 'image'
  const [rawText, setRawText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    department_id: '',
    owner_name: '',
    owner_role: '',
    description: '',
    objective: '',
    systems_used: [],
    frequency: 'Daily',
    volume_per_period: '',
    avg_cycle_time_minutes: '',
    error_rate_percent: '',
    pain_points: []
  });

  const [newSystem, setNewSystem] = useState('');
  const [newPainPoint, setNewPainPoint] = useState('');

  useEffect(() => {
    if (!isReady) return;
    fetchDepartments();
    if (isEditMode) {
      fetchProcess();
    }
  }, [id, isReady]);

  const fetchProcess = async () => {
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/org/processes/${id}`, {}, activeOrganization?.organization_id);
      if (response.ok) {
        const data = await response.json();
        const proc = data.process;
        setFormData({
          name: proc.name || '',
          department_id: proc.department_id || '',
          owner_name: proc.owner_name || '',
          owner_role: proc.owner_role || '',
          description: proc.description || '',
          objective: proc.objective || '',
          systems_used: proc.systems_used || [],
          frequency: proc.frequency || 'Daily',
          volume_per_period: proc.volume_per_period || '',
          avg_cycle_time_minutes: proc.avg_cycle_time_minutes || '',
          error_rate_percent: proc.error_rate_percent || '',
          pain_points: proc.pain_points || []
        });
      }
    } catch (error) {
      console.error('Error fetching process:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/org/departments`, {}, activeOrganization?.organization_id);
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Initiate extraction - proceed directly without confirmation modal
  const initiateExtraction = (type) => {
    if (type === 'text') {
      executeTextExtraction();
    } else {
      executeImageExtraction();
    }
  };

  // Execute text extraction
  const executeTextExtraction = async () => {
    if (!rawText.trim()) {
      alert('Please enter some text to extract');
      return;
    }

    setExtracting(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/org/processes/extract-from-text`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ raw_text: rawText })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setExtractedData(data);
        
        // Pre-fill form with extracted data
        setFormData({
          ...formData,
          name: data.name || formData.name,
          description: data.description || formData.description,
          objective: data.objective || formData.objective
        });
      } else {
        alert('Failed to extract process. Please try again.');
      }
    } catch (error) {
      console.error('Error extracting process:', error);
      alert('Failed to extract process. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  // Execute image extraction
  const executeImageExtraction = async () => {
    if (!selectedImage) {
      alert('Please select an image first');
      return;
    }

    setExtracting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', selectedImage);
      formDataUpload.append('organization_id', activeOrganization?.organization_id);

      const response = await fetch(`${BACKEND_URL}/api/org/processes/extract-from-image`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setExtractedData(data);
        
        // Pre-fill form with extracted data
        setFormData({
          ...formData,
          name: data.name || formData.name,
          description: data.description || formData.description,
          objective: data.objective || formData.objective
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        const message = typeof errData.detail === 'string' ? errData.detail : 'Failed to extract process from image. Please try again.';
        alert(message);
      }
    } catch (error) {
      console.error('Error extracting process from image:', error);
      alert('Failed to extract process from image. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  // Legacy function for backwards compatibility (redirects to new flow)
  const handleExtractFromText = () => {
    initiateExtraction('text');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (PNG, JPEG, etc.)');
        return;
      }
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Legacy handleExtractFromImage - now redirects to new flow
  const handleExtractFromImage = () => {
    initiateExtraction('image');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        volume_per_period: formData.volume_per_period ? parseInt(formData.volume_per_period) : null,
        avg_cycle_time_minutes: formData.avg_cycle_time_minutes ? parseFloat(formData.avg_cycle_time_minutes) : null,
        error_rate_percent: formData.error_rate_percent ? parseFloat(formData.error_rate_percent) : null
      };

      if (isEditMode) {
        // Update existing process
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/org/processes/${id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          },
          activeOrganization?.organization_id
        );

        if (!response.ok) {
          throw new Error('Failed to update process');
        }

        navigate(`/processes/${id}`);
      } else {
        // Create new process
        const processResponse = await fetchWithOrg(
          `${BACKEND_URL}/api/org/processes`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          },
          activeOrganization?.organization_id
        );

        if (!processResponse.ok) {
          throw new Error('Failed to create process');
        }

        const createdProcess = await processResponse.json();

        // If we have extracted steps, create them
        if (extractedData?.steps) {
          for (const step of extractedData.steps) {
            // Sanitize step data for API validation
            const sanitizedStep = {
              title: step.title || 'Untitled Step',
              order_index: typeof step.order_index === 'number' ? step.order_index : 1,
              description: step.description || null,
              actor_role: step.actor_role || null,
              system: step.system || null,
              avg_time_minutes: typeof step.avg_time_minutes === 'number' ? step.avg_time_minutes : (parseFloat(step.avg_time_minutes) || null),
              manual_or_automated: step.manual_or_automated || 'manual',
              pain_points: Array.isArray(step.pain_points) ? step.pain_points : []
            };
            try {
              await fetchWithOrg(
                `${BACKEND_URL}/api/org/processes/${createdProcess.id}/steps`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(sanitizedStep)
                },
                activeOrganization?.organization_id
              );
            } catch (stepErr) {
              console.warn('Failed to create step:', step.title, stepErr);
            }
          }
        }

        navigate(`/processes/${createdProcess.id}`);
      }
    } catch (error) {
      console.error('Error saving process:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} process. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const addSystem = () => {
    if (newSystem.trim()) {
      setFormData({ ...formData, systems_used: [...formData.systems_used, newSystem.trim()] });
      setNewSystem('');
    }
  };

  const removeSystem = (index) => {
    setFormData({ ...formData, systems_used: formData.systems_used.filter((_, i) => i !== index) });
  };

  const addPainPoint = () => {
    if (newPainPoint.trim()) {
      setFormData({ ...formData, pain_points: [...formData.pain_points, newPainPoint.trim()] });
      setNewPainPoint('');
    }
  };

  const removePainPoint = (index) => {
    setFormData({ ...formData, pain_points: formData.pain_points.filter((_, i) => i !== index) });
  };

  return (
    <>
      <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={() => navigate('/processes')}
                className="flex items-center gap-2 text-[#A0AEC0] hover:text-white transition-colors mb-4"
              >
                <ArrowLeft size={20} />
                Back to Processes
              </button>
              <h1 className="text-4xl font-bold text-white mb-2">
                {isEditMode ? 'Edit' : 'Create New'} <span className="text-[#6366F1]">Process</span>
              </h1>
              <p className="text-[#A0AEC0]">
                {isEditMode ? 'Update your business process details or use AI extraction to add more steps' : 'Define your business process or let AI extract it from text'}
              </p>
            </div>

            {/* AI Extraction Panel - Available for both new and editing processes */}
            <div className="glass rounded-3xl p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Sparkle size={24} className="text-[#6366F1]" />
                  <h2 className="text-xl font-bold text-white">AI Process Extraction</h2>
                </div>
                <button
                  onClick={() => setShowAIExtraction(!showAIExtraction)}
                  className="text-[#6366F1] text-sm font-medium"
                >
                  {showAIExtraction ? 'Hide' : 'Show'}
                </button>
              </div>

              {showAIExtraction && (
                <div>
                  {/* Extraction Method Tabs */}
                  <div className="flex gap-2 mb-6">
                    <button
                      onClick={() => setExtractionMethod('text')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        extractionMethod === 'text'
                          ? 'bg-[#6366F1] text-white'
                          : 'glass text-[#A0AEC0] hover:text-white'
                      }`}
                    >
                      <FileText size={16} />
                      From Text (SOP)
                    </button>
                    <button
                      onClick={() => setExtractionMethod('image')}
                      disabled={isBasicUser}
                      title={isBasicUser ? 'Process diagram analysis is available on Builder and higher plans.' : ''}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        isBasicUser
                          ? 'glass text-[#555] cursor-not-allowed opacity-50'
                          : extractionMethod === 'image'
                          ? 'bg-[#6366F1] text-white'
                          : 'glass text-[#A0AEC0] hover:text-white'
                      }`}
                    >
                      <ImageIcon size={16} />
                      From Diagram (Image)
                      {isBasicUser && <Lock size={12} className="ml-1" />}
                    </button>
                  </div>

                  {/* Credit cost estimate */}
                  {extractionMethod === 'text' && (
                    <CreditCostEstimate cost={10} label="process analysis" className="mt-4" />
                  )}
                  {extractionMethod === 'image' && !isBasicUser && (
                    <CreditCostEstimate cost={30} label="diagram analysis" className="mt-4" />
                  )}

                  {/* Text Extraction */}
                  {extractionMethod === 'text' && (
                    <div>
                      <p className="text-[#A0AEC0] mb-4 text-sm">
                        Paste your existing SOP, process description, or steps. AI will extract the structure for you.
                      </p>
                      <textarea
                        value={rawText}
                        onChange={(e) => setRawText(e.target.value)}
                        placeholder="Example: Our lead handling process starts when a lead submits the contact form. First, the sales rep receives an email notification. Then they review the lead in our CRM system. Next, they call the lead within 24 hours. Finally, they update the lead status based on the call outcome."
                        rows={6}
                        className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none resize-none"
                      />
                      <button
                        onClick={handleExtractFromText}
                        disabled={extracting || !rawText.trim()}
                        className="mt-4 rounded-full bg-[#6366F1] hover:bg-[#5558E6] disabled:opacity-50 transition-colors"
                      >
                        <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                          {extracting ? (
                            <><CircleNotch size={20} className="animate-spin" /> Extracting...</>
                          ) : (
                            <><Sparkle size={20} /> Extract From Text</>
                          )}
                        </span>
                      </button>
                    </div>
                  )}

                  {/* Image Extraction */}
                  {extractionMethod === 'image' && !isBasicUser && (
                    <div>
                      <p className="text-[#A0AEC0] mb-3 text-sm">
                        UploadSimple a flowchart or process diagram. The AI will attempt to extract steps and structure.
                      </p>
                      
                      {/* Credit cost estimate */}
                      <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.2)] text-sm" data-testid="credit-cost-estimate-process">
                        <span className="text-[#A0AEC0]">Estimated AI analysis cost: <span className="text-white font-semibold">~60 credits</span></span>
                      </div>
                      
                      {/* Image UploadSimple Area */}
                      <div className="mb-4">
                        <label className="block">
                          <div className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                            imagePreview 
                              ? 'border-[#14F195]/50 bg-[#14F195]/5' 
                              : 'border-[rgba(255,255,255,0.2)] hover:border-[#6366F1]/50 hover:bg-[rgba(99,102,241,0.05)]'
                          }`}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="hidden"
                            />
                            {imagePreview ? (
                              <div>
                                <img 
                                  src={imagePreview} 
                                  alt="Preview" 
                                  className="max-h-64 mx-auto rounded-xl mb-4"
                                />
                                <p className="text-[#14F195] font-medium">✓ Image selected</p>
                                <p className="text-[#A0AEC0] text-sm mt-1">Click to change</p>
                              </div>
                            ) : (
                              <div>
                                <UploadSimple size={48} className="text-[#6366F1] mx-auto mb-4" />
                                <p className="text-white font-medium mb-2">Click to upload diagram</p>
                                <p className="text-[#A0AEC0] text-sm">PNG, JPEG, or other image formats</p>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>

                      <button
                        onClick={handleExtractFromImage}
                        disabled={extracting || !selectedImage}
                        className="rounded-full bg-[#6366F1] hover:bg-[#5558E6] disabled:opacity-50 transition-colors"
                      >
                        <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                          {extracting ? (
                            <><CircleNotch size={20} className="animate-spin" /> Analyzing Image...</>
                          ) : (
                            <><Sparkle size={20} /> Extract From Image</>
                          )}
                        </span>
                      </button>
                    </div>
                  )}

                  {/* Extraction Success Message */}
                  {extractedData && (
                    <div className="mt-6 p-4 rounded-xl bg-[#14F195]/10 border border-[#14F195]/30">
                      <p className="text-[#14F195] font-medium mb-2">✓ Extraction Complete</p>
                      <p className="text-[#A0AEC0] text-sm">
                        AI extracted {extractedData.steps?.length || 0} steps from your {extractionMethod === 'image' ? 'diagram' : 'text'}. 
                        Process name and details have been pre-filled below.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="glass rounded-3xl p-8">
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <label className="block text-white font-semibold mb-2">Process Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                    placeholder="e.g., Lead Qualification Process"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-semibold mb-2">Department</label>
                    <select
                      value={formData.department_id}
                      onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Frequency</label>
                    <select
                      value={formData.frequency}
                      onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    >
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Ad hoc">Ad hoc</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-semibold mb-2">Owner Name</label>
                    <input
                      type="text"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Owner Role</label>
                    <input
                      type="text"
                      value={formData.owner_role}
                      onChange={(e) => setFormData({ ...formData, owner_role: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., Sales Manager"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none resize-none"
                    placeholder="Brief description of the process"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Objective</label>
                  <textarea
                    value={formData.objective}
                    onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none resize-none"
                    placeholder="What does this process aim to achieve?"
                  />
                </div>

                {/* Metrics */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-white font-semibold mb-2">Volume per Period</label>
                    <input
                      type="number"
                      value={formData.volume_per_period}
                      onChange={(e) => setFormData({ ...formData, volume_per_period: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., 200"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Avg Cycle Time (min)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.avg_cycle_time_minutes}
                      onChange={(e) => setFormData({ ...formData, avg_cycle_time_minutes: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., 15"
                    />
                  </div>

                  <div>
                    <label className="block text-white font-semibold mb-2">Error Rate (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.error_rate_percent}
                      onChange={(e) => setFormData({ ...formData, error_rate_percent: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., 5"
                    />
                  </div>
                </div>

                {/* Systems Used */}
                <div>
                  <label className="block text-white font-semibold mb-2">Systems Used</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newSystem}
                      onChange={(e) => setNewSystem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSystem())}
                      className="flex-1 px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., CRM, Email, Spreadsheet"
                    />
                    <button
                      type="button"
                      onClick={addSystem}
                      className="px-4 py-3 rounded-xl glass hover:bg-[rgba(255,255,255,0.1)] text-white"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.systems_used.map((system, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#6366F1]/20 text-[#6366F1] text-sm">
                        {system}
                        <button type="button" onClick={() => removeSystem(index)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pain Points */}
                <div>
                  <label className="block text-white font-semibold mb-2">Pain Points</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newPainPoint}
                      onChange={(e) => setNewPainPoint(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPainPoint())}
                      className="flex-1 px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:border-[#6366F1] outline-none"
                      placeholder="e.g., Manual data entry, Time consuming"
                    />
                    <button
                      type="button"
                      onClick={addPainPoint}
                      className="px-4 py-3 rounded-xl glass hover:bg-[rgba(255,255,255,0.1)] text-white"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.pain_points.map((pain, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#EF4444]/20 text-[#EF4444] text-sm">
                        {pain}
                        <button type="button" onClick={() => removePainPoint(index)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Extracted Steps Preview */}
                {extractedData?.steps?.length > 0 && (
                  <div>
                    <label className="block text-white font-semibold mb-3">
                      Extracted Steps ({extractedData.steps.length})
                    </label>
                    <div className="space-y-2">
                      {extractedData.steps.map((step, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)]"
                        >
                          <div className="w-7 h-7 rounded-lg bg-[#6366F1] flex items-center justify-center flex-shrink-0 text-xs font-bold text-white">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{step.title || 'Untitled Step'}</p>
                            {step.description && (
                              <p className="text-xs text-[#718096] mt-0.5 line-clamp-2">{step.description}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              {step.actor_role && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(99,102,241,0.1)] text-[#A0AEC0]">{step.actor_role}</span>
                              )}
                              {step.system && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(99,102,241,0.1)] text-[#A0AEC0]">{step.system}</span>
                              )}
                              {step.manual_or_automated && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${step.manual_or_automated === 'manual' ? 'bg-[rgba(239,68,68,0.1)] text-[#EF4444]' : 'bg-[rgba(20,241,149,0.1)] text-[#14F195]'}`}>
                                  {step.manual_or_automated}
                                </span>
                              )}
                              {step.avg_time_minutes && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(255,255,255,0.05)] text-[#718096]">{step.avg_time_minutes} min</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#555] mt-2">These steps will be saved when you create the process.</p>
                  </div>
                )}

                {/* Submit */}
                <div className="flex gap-4 justify-end pt-6 border-t border-[rgba(255,255,255,0.1)]">
                  <button
                    type="button"
                    onClick={() => navigate('/processes')}
                    className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-full bg-[#6366F1] hover:bg-[#5558E6] disabled:opacity-50 transition-colors"
                  >
                    <span className="flex items-center gap-2 px-6 py-3 text-white font-bold">
                      {loading ? (
                        <><CircleNotch size={20} className="animate-spin" /> {isEditMode ? 'Updating...' : 'Creating...'}</>
                      ) : (
                        <><FloppyDisk size={20} /> {isEditMode ? 'Update Process' : 'Create Process'}</>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>

        {/* Analysis Confirmation Modal */}
    </>
  );
};

export default CreateProcess;